/*
  Sainté — anbefalingsmotor til Size Finder
  ==================================================================
  Ren beregning. Rører ikke DOM'en, kender ikke til modalen, og kan
  derfor køres i Node til test. UI'et ligger i sainte-size-finder.js.

  Alle tal om varen kommer fra produktets størrelsesguide (metaobjektet
  custom.storrelsesguide). Intet størrelsesskema er hardkodet her.

  MÅLEKONVENTION
  --------------
  Måleskitsen på longsleeven viser at "Chest" er den flade bredde fra
  ærmegab til ærmegab. Omkredsen er derfor 2 x tallet. Står der
  measurementType: 'body' i guiden, er tallene kropsmål og bruges som de er.
  Gætter vi forkert her, fordobler vi et mål og anbefaler vanvid — derfor
  afvises en guide hvis tallene er urimelige for den erklærede type.

  HVORFOR SVARENE VÆGTER SOM DE GØR
  ---------------------------------
  - Plejer-størrelse er et prior, ikke en sandhed. Vanity sizing gør en M
    forskellig fra brand til brand, så den får en usikkerhed med og må
    højst flytte sig to trin.
  - Højde styrer længde og ærme, aldrig bredde. Brystomkreds og højde
    varierer uafhængigt (torso/højde R² ca. 0,40), mens armspænd følger
    højden tæt (R ca. 0,84).
  - Maveform rammer taljen, ikke brystet. På et lige snit er varens
    nederste bredde den samme som brystbredden, så en rund mave kan gøre
    taljen til det bindende mål i stedet for brystet.
  - For stram straffes hårdere end for løs. En vare der strammer kan ikke
    bæres; en der er lidt for stor kan.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) { module.exports = api; }
  root.SainteSizeEngine = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* Tekster fra locales (sainte.sf.*), se sainte-size-finder.js. Dansk er
     standard, og i Node (tests) findes window ikke. */
  var I18N = (typeof window !== 'undefined' && window.SAINTE_SF_I18N) || {};
  function L(k, d, v) {
    var s = I18N[k] || d;
    return v ? String(s).replace(/%(\w+)%/g, function (m, x) { return v[x] != null ? v[x] : m; }) : s;
  }

  var SIZE_ORDER = ['3XS', '2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

  /* Antropometriske kropsmål i cm for "den størrelse du plejer at bruge".
     EU-tabeller i EN13402-familien. Ligger som data, ikke som logik, så de
     kan justeres når vi har vores egne returdata at kalibrere på. */
  var BODY = {
    men: {
      chest: { XS: 84, S: 91.5, M: 98.5, L: 106, XL: 114, '2XL': 122 },
      waist: { XS: 72, S: 78, M: 85, L: 92, XL: 100, '2XL': 108 },
      chestStep: 7.5, waistStep: 7.2
    },
    women: {
      chest: { XS: 79, S: 85, M: 91.5, L: 98.5, XL: 106, '2XL': 114 },
      waist: { XS: 62, S: 68, M: 74, L: 81, XL: 89, '2XL': 97 },
      chestStep: 6.75, waistStep: 7.0
    }
  };

  /* Mål-ease ved brystet i cm omkreds, pr. varens tilsigtede pasform.
     relaxed = 15 cm ligger i det interval faglitteraturen angiver for
     relaxed fit, og det er dét der reproducerer Semih-ankeret: en mand der
     bruger L får L, ikke XL. Højere værdier skubbede systematisk alle
     L-brugere op i XL. */
  var BASE_EASE = { slim: 6, regular: 10, relaxed: 15, oversized: 24 };
  var FIT_PREF = { tighter: -5, normal: 0, looser: 6 };

  /* Samme vare bæres med forskellig rummelighed af de to grupper. Snittet
     er unisex/herre, så selv XS er 108 cm i omkreds; kvinder køber den
     bevidst oversized. Brandets egne modellinjer er ankrene:
       Semih, 185 cm, bruger L  -> ca. 16 cm ease
       Julie, 164 cm, bruger S  -> ca. 27 cm ease
     Uden dette led fik en kvinde der bruger M besked på at tage XS, hvilket
     modsiger Julie. ADVARSEL: ét ankerpunkt pr. køn. Skal efterkalibreres,
     når der findes rigtige pasformsdata. */
  var EASE_BY_GENDER = { men: 0, women: 10 };

  /* Hvor kunden ligger i sit eget størrelsesbånd, i andele af et trin. */
  var CONSISTENCY = { smaller: -0.30, always: 0, larger: 0.30 };
  var UPPER_BODY = { narrow: -0.45, average: 0, broad: 0.45 };
  var BELLY = { flat: -0.60, average: 0, round: 1.80 };

  /* En rund mave æder frontlængde: stoffet skydes udad og hemmen kryber op.
     På et bokset snit er det den effekt maveformen faktisk har — bredden
     er alligevel rigelig. Værdier i cm lagt til den ønskede varelængde. */
  var BELLY_LENGTH = { flat: -0.5, average: 0, round: 2.5 };

  var MIN_EASE = 4;            // under dette kan varen fysisk ikke bæres
  /* Og over dette drukner kunden i varen. Grænserne er FYSISKE — en vare
     der bare er mindre oversized end idealet, er stadig en vare der passer,
     og må ikke give "ingen anbefaling". Tidligere afviste vi på stilafvigelse
     alene, og 18% af kvinderne fik intet svar, selv om XL sad fint på dem. */
  var MAX_EASE = 34;
  var COMFORT_WAIST_EASE = 12; // et bokset snit skal falde frit over taljen
  var MAX_PRIOR_DRIFT = 2;     // højst to trin fra plejer-størrelsen

  var W = { chest: 0.55, length: 0.20, sleeve: 0.10, waist: 0.15 };
  var TOL = { chest: 7, length: 4.5, sleeve: 3.5, waist: 8 };
  var TIGHT_PENALTY = 1.4;
  var PRIOR_PENALTY = 0.18;

  /* Kalibreret på brandets egen modellinje: Semih er 185 cm og bruger L,
     som er 70 cm lang med 80 cm ærme. */
  var K_LENGTH = 70 / 185;
  var K_SLEEVE = 80 / 185;

  /* Sat højt med vilje: afvisning skal komme fra de fysiske grænser
     ovenfor, ikke fra at pasformen afviger fra kundens ønske. */
  var REJECT_SCORE = 1.9;
  var HIGH_SCORE = 0.75;
  var CLEAR_MARGIN = 0.25;
  var TIE_MARGIN = 0.18;

  function idx(size) { return SIZE_ORDER.indexOf(String(size).toUpperCase()); }

  /* Slår et kropsmål op og ekstrapolerer uden for tabellen, så en vare der
     kører 2XS eller 3XL ikke falder på gulvet. */
  function lookupBody(table, step, size) {
    var s = String(size).toUpperCase();
    if (table[s] != null) return table[s];
    var known = Object.keys(table).filter(function (k) { return idx(k) >= 0; })
      .sort(function (a, b) { return idx(a) - idx(b); });
    if (!known.length) return null;
    var lo = known[0], hi = known[known.length - 1];
    var i = idx(s);
    if (i < 0) return null;
    if (i < idx(lo)) return table[lo] - step * (idx(lo) - i);
    return table[hi] + step * (i - idx(hi));
  }

  function num(v) {
    if (typeof v === 'number') return isFinite(v) ? v : null;
    if (v == null) return null;
    var m = String(v).replace(',', '.').match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  }

  /* Omregner varens tal til kropsomkreds i cm. */
  function circumference(value, guide) {
    var v = num(value);
    if (v == null) return null;
    if (guide.unit === 'mm') v = v / 10;
    return guide.measurementType === 'body' ? v : v * 2;
  }

  function linear(value, guide) {
    var v = num(value);
    if (v == null) return null;
    return guide.unit === 'mm' ? v / 10 : v;
  }

  /* Guiden skal give mening for den type den påstår at være. Et bryst på
     54 cm er en flad bredde; 108 er en omkreds. Passer tallene ikke til
     measurementType, er guiden forkert udfyldt og vi anbefaler ikke. */
  function guideIsPlausible(guide) {
    var vals = guide.sizes.map(function (s) { return num(s.measurements.chest); })
      .filter(function (v) { return v != null; });
    if (!vals.length) return false;
    var mid = vals.sort(function (a, b) { return a - b; })[Math.floor(vals.length / 2)];
    if (guide.unit === 'mm') mid = mid / 10;
    return guide.measurementType === 'body'
      ? (mid >= 70 && mid <= 160)
      : (mid >= 30 && mid <= 90);
  }

  function estimateBody(answers) {
    var g = BODY[answers.gender] ? answers.gender : 'men';
    var t = BODY[g];
    var cons = CONSISTENCY[answers.consistency] != null ? CONSISTENCY[answers.consistency] : 0;
    var upper = UPPER_BODY[answers.upperBody] != null ? UPPER_BODY[answers.upperBody] : 0;
    var belly = BELLY[answers.belly] != null ? BELLY[answers.belly] : 0;

    var baseChest = lookupBody(t.chest, t.chestStep, answers.usualSize);
    var baseWaist = lookupBody(t.waist, t.waistStep, answers.usualSize);
    if (baseChest == null) return null;

    /* Vanity sizing gør plejer-størrelsen upræcis. Er kunden selv i tvivl
       om den, vokser usikkerheden — og den bruges bagefter til at afgøre
       om anbefalingen overhovedet er stabil. */
    var sigma = answers.consistency === 'always' ? 3.0 : 4.5;

    return {
      chest: baseChest + t.chestStep * (cons + upper),
      waist: baseWaist + t.waistStep * (cons + belly),
      sigma: sigma,
      wantLength: K_LENGTH * answers.height + (BELLY_LENGTH[answers.belly] || 0),
      wantSleeve: K_SLEEVE * answers.height
    };
  }

  function scoreSize(entry, body, answers, guide, usualIdx) {
    var m = entry.measurements;
    var chestCirc = circumference(m.chest, guide);
    if (chestCirc == null) return null;

    /* En guide kan angive sin egen mål-ease i cm og dermed tilsidesætte
       tabellen. Bruges til at kalibrere mod virkeligheden: hvad folk rent
       faktisk køber og beholder, slår hvad en lærebog siger om "relaxed". */
    var base = guide.targetEase != null ? Number(guide.targetEase)
      : (BASE_EASE[guide.intendedFit] != null ? BASE_EASE[guide.intendedFit] : BASE_EASE.relaxed);
    var target = base
      + (FIT_PREF[answers.fitPreference] != null ? FIT_PREF[answers.fitPreference] : 0)
      + (EASE_BY_GENDER[answers.gender] || 0);

    var easeChest = chestCirc - body.chest;
    /* Lige snit: nederste bredde er den samme som brystbredden, med mindre
       guiden selv oplyser en hem-bredde. */
    var hemCirc = m.hem != null ? circumference(m.hem, guide) : chestCirc;
    var easeWaist = hemCirc - body.waist;

    if (easeChest < MIN_EASE || easeWaist < MIN_EASE) {
      return { rejected: true, reason: 'too_small' };
    }
    if (easeChest > MAX_EASE) {
      return { rejected: true, reason: 'too_large' };
    }

    var pChest = Math.abs(easeChest - target) / TOL.chest;
    if (easeChest < target) pChest *= TIGHT_PENALTY;

    /* Taljen scorer kun når den klemmer. En boks-t-shirt må gerne være
       rigelig forneden — det er meningen. */
    var pWaist = Math.max(0, COMFORT_WAIST_EASE - easeWaist) / TOL.waist;

    var pLen = 0, pSlv = 0, wLen = 0, wSlv = 0, lengthDelta = null;
    var len = linear(m.length, guide);
    if (len != null) {
      lengthDelta = len - body.wantLength;
      pLen = Math.abs(lengthDelta) / TOL.length; wLen = W.length;
    }
    var slv = linear(m.sleeve, guide);
    if (slv != null) { pSlv = Math.abs(slv - body.wantSleeve) / TOL.sleeve; wSlv = W.sleeve; }

    var wSum = W.chest + W.waist + wLen + wSlv;
    var total = (W.chest * pChest + W.waist * pWaist + wLen * pLen + wSlv * pSlv) / wSum;

    /* Plejer-størrelsen bærer rigtig information om hvordan kunden kan
       lide at gå klædt. Uden dette led kan tre spørgsmål tilsammen skubbe
       en M-bruger helt til XL, hvilket ingen kunde accepterer. */
    var drift = usualIdx >= 0 ? Math.abs(idx(entry.size) - usualIdx) : 0;
    if (usualIdx >= 0 && drift > MAX_PRIOR_DRIFT) return { rejected: true, reason: 'too_far_from_usual' };
    total += PRIOR_PENALTY * drift;

    return {
      rejected: false, score: total, size: entry.size,
      easeChest: Math.round(easeChest * 10) / 10,
      easeWaist: Math.round(easeWaist * 10) / 10,
      target: target, drift: drift,
      lengthDelta: lengthDelta == null ? null : Math.round(lengthDelta * 10) / 10
    };
  }

  function rank(entries, body, answers, guide, usualIdx) {
    var out = [];
    for (var i = 0; i < entries.length; i++) {
      var r = scoreSize(entries[i], body, answers, guide, usualIdx);
      if (r && !r.rejected) out.push(r);
    }
    out.sort(function (a, b) { return a.score - b.score; });
    return out;
  }

  /*
    recommend(input) -> { status, size, confidence, alternativeSize, reason, debug }

    status:
      recommended             en størrelse matcher tydeligt
      borderline              kunden ligger mellem to størrelser
      recommended_but_sold_out den rigtige størrelse er udsolgt
      no_suitable_size        ingen tilgængelig størrelse kan forsvares
  */
  function recommend(input) {
    var guide = input.guide || {};
    var answers = input.answers || {};

    var g = {
      sizes: guide.sizes || [],
      measurementType: guide.measurementType || 'garment_flat',
      intendedFit: guide.intendedFit || 'relaxed',
      unit: guide.unit || 'cm',
      category: guide.category || 'tops',
      /* Skal føres med — ellers falder en guide med egen mål-ease tavst
         tilbage til tabelværdien, og kalibreringen har ingen effekt. */
      targetEase: guide.targetEase
    };

    if (!g.sizes.length || !guideIsPlausible(g)) {
      return fail('guide_unusable', L('reason_guide_unusable', 'Størrelsesguiden kan ikke bruges til en beregning.'));
    }

    var offered = input.offered && input.offered.length
      ? input.offered
      : g.sizes.map(function (s) { return s.size; });
    var available = input.available || offered;

    var entries = g.sizes.filter(function (s) { return offered.indexOf(s.size) !== -1; });
    if (!entries.length) return fail('guide_unusable', L('reason_guide_sizes', 'Størrelsesguiden dækker ikke varens størrelser.'));

    var body = estimateBody(answers);
    if (!body) return fail('guide_unusable', L('reason_parse', 'Kunne ikke tolke svarene.'));

    var usualIdx = idx(answers.usualSize);
    var ranked = rank(entries, body, answers, g, usualIdx);
    if (!ranked.length) {
      return fail('no_suitable_size', L('reason_none', 'Ingen af størrelserne kan rumme dine mål med en forsvarlig pasform.'));
    }

    var best = ranked[0];
    var runner = ranked[1] || null;
    var margin = runner ? runner.score - best.score : 99;

    /* Er anbefalingen stabil hvis kroppen ligger en usikkerhed væk? Flipper
       den, ligger kunden reelt mellem to størrelser, og det skal vi sige
       højt i stedet for at lade som om vi er sikre. */
    var flips = {};
    [-body.sigma, body.sigma].forEach(function (d) {
      var alt = rank(entries, {
        chest: body.chest + d, waist: body.waist + d, sigma: body.sigma,
        wantLength: body.wantLength, wantSleeve: body.wantSleeve
      }, answers, g, usualIdx);
      if (alt.length) flips[alt[0].size] = true;
    });
    var stable = Object.keys(flips).every(function (s) { return s === best.size; });

    if (best.score > REJECT_SCORE) {
      return fail('no_suitable_size', L('reason_outside', 'Dine mål ligger uden for det, denne vare kan passe ordentligt.'));
    }

    var status, confidence;
    if (stable && margin > CLEAR_MARGIN && best.score < HIGH_SCORE) {
      status = 'recommended'; confidence = 'high';
    } else if (!stable || margin < TIE_MARGIN) {
      status = 'borderline'; confidence = best.score > 0.9 ? 'low' : 'medium';
    } else {
      status = 'recommended'; confidence = 'medium';
    }

    /* En størrelse kan godt være den bedste af de mulige og stadig sidde
       langt fra det kunden bad om. Så skal vi sige det, ikke pynte på det
       med "medium sikkerhed". */
    if (best.score > 1.1) confidence = 'low';

    /* Den bedste størrelse findes, men er udsolgt. Vi lader være med at
       udnævne nummer to til at passe lige så godt. */
    var alternative = runner ? runner.size : null;
    if (available.indexOf(best.size) === -1) {
      var inStock = ranked.filter(function (r) { return available.indexOf(r.size) !== -1; });
      return {
        status: 'recommended_but_sold_out',
        size: best.size,
        confidence: confidence,
        alternativeSize: inStock.length ? inStock[0].size : null,
        reason: L('reason_sold_out', 'Vi anbefaler %size%, men den er desværre udsolgt.', { size: best.size }),
        debug: debugOf(best, body, margin, flips)
      };
    }

    return {
      status: status,
      size: best.size,
      confidence: confidence,
      alternativeSize: status === 'borderline' && alternative && available.indexOf(alternative) !== -1
        ? alternative : null,
      reason: reasonFor(status, best, alternative, g),
      debug: debugOf(best, body, margin, flips)
    };
  }

  /* Længden kan være det svage punkt, selv når bredden passer — typisk på
     høje kunder. Det siger vi højt i stedet for at lade det være en
     overraskelse ved udpakning. */
  function lengthNote(best) {
    if (best.lengthDelta == null) return '';
    if (best.lengthDelta <= -4) return (' ' + L('note_short', 'Bemærk at den vil sidde kort på dig.'));
    if (best.lengthDelta >= 5) return (' ' + L('note_long', 'Bemærk at den vil sidde lang på dig.'));
    return '';
  }

  function reasonFor(status, best, alternative, g) {
    if (status === 'borderline' && alternative) {
      var looser = idx(alternative) > idx(best.size);
      return (looser
        ? L('between_looser', 'Du ligger mellem to størrelser. Vil du have den mere oversized, så vælg %alt%.', { alt: alternative })
        : L('between_tighter', 'Du ligger mellem to størrelser. Vil du have den strammere, så vælg %alt%.', { alt: alternative }))
        + lengthNote(best);
    }
    var slack = best.easeChest - best.target;
    if (slack > 10) {
      return L('nearest_looser', 'Det er den nærmeste størrelse, men den vil sidde markant løsere, end du har bedt om.') + lengthNote(best);
    }
    if (slack < -6) {
      return L('nearest_tighter', 'Det er den nærmeste størrelse, men den vil sidde tættere, end du har bedt om.') + lengthNote(best);
    }
    return L('fits', 'Målene på denne vare passer til dine oplysninger i størrelse %size%.', { size: best.size }) + lengthNote(best);
  }

  function debugOf(best, body, margin, flips) {
    return {
      score: Math.round(best.score * 100) / 100,
      margin: margin === 99 ? null : Math.round(margin * 100) / 100,
      easeChest: best.easeChest, easeWaist: best.easeWaist, targetEase: best.target,
      bodyChest: Math.round(body.chest * 10) / 10,
      bodyWaist: Math.round(body.waist * 10) / 10,
      drift: best.drift, flips: Object.keys(flips)
    };
  }

  function fail(status, reason) {
    return { status: status, size: null, confidence: 'low', alternativeSize: null, reason: reason, debug: null };
  }

  /* ------------------------------------------------------------------
     Tolkning af størrelsesguiden
     ------------------------------------------------------------------
     Guiden er skrevet af mennesker i Shopify Admin, så kolonnen kan hedde
     "Chest", "Bryst" eller "Bredde". Vi oversætter her ét sted i stedet
     for at kræve, at alle eksisterende guides skrives om.
  */
  var SYNONYMS = {
    size:   ['size', 'størrelse', 'storrelse', 'str'],
    chest:  ['chest', 'bryst', 'brystvidde', 'bredde', 'width', 'bust', 'barm'],
    length: ['length', 'længde', 'laengde', 'longueur'],
    sleeve: ['sleeve', 'sleeve length', 'ærme', 'aerme', 'ærmelængde', 'aermelaengde'],
    waist:  ['waist', 'talje', 'livvidde'],
    hip:    ['hip', 'hofte', 'hoftevidde'],
    inseam: ['inseam', 'indvendig benlængde', 'skridtlængde', 'benlængde'],
    thigh:  ['thigh', 'lår', 'laar'],
    hem:    ['hem', 'bund', 'bundvidde', 'bottom']
  };

  function normalise(s) {
    return String(s).toLowerCase().replace(/\([^)]*\)/g, '').replace(/[^a-zæøå ]/gi, '').trim();
  }

  function fieldFor(header) {
    var h = normalise(header);
    if (!h) return null;
    var best = null, bestLen = 0;
    for (var key in SYNONYMS) {
      for (var i = 0; i < SYNONYMS[key].length; i++) {
        var syn = SYNONYMS[key][i];
        if (h === syn || h.indexOf(syn) !== -1) {
          if (syn.length > bestLen) { best = key; bestLen = syn.length; }
        }
      }
    }
    return best;
  }

  /*
    parseGuide({ columns, rows, intendedFit, measurementType, unit })
    columns: "Size | Length (cm) | Chest (cm) | Sleeve length (cm)"
    rows:    "XS | 64 | 54 | 77\nS | 66 | 56 | 78"
  */
  function parseGuide(raw) {
    if (!raw) return null;
    /* Er guiden allerede tolket, gives den uændret tilbage. Ellers ville en
       ekstra kørsel af opsætningen — fx hvis scriptet indlæses to gange —
       få den til at se en guide uden kolonner og melde den ubrugelig. */
    if (Array.isArray(raw.sizes) && raw.sizes.length) return raw;

    var cols = String(raw.columns || '').split('|').map(function (c) { return c.trim(); });
    if (cols.length < 2) return null;

    var fields = cols.map(fieldFor);
    if (fields.indexOf('size') === -1) fields[0] = 'size';

    /* Enheden står typisk i kolonneoverskriften. Totebag-guiden skriver mm. */
    var unit = raw.unit;
    if (!unit) unit = /\bmm\b/i.test(raw.columns || '') || /millimeter/i.test(raw.note || '') ? 'mm' : 'cm';

    var sizes = [];
    String(raw.rows || '').split(/\r?\n/).forEach(function (line) {
      if (!line.trim()) return;
      var cells = line.split('|').map(function (c) { return c.trim(); });
      var entry = { size: null, measurements: {} };
      for (var i = 0; i < cells.length && i < fields.length; i++) {
        if (fields[i] === 'size') entry.size = cells[i].toUpperCase();
        else if (fields[i]) entry.measurements[fields[i]] = cells[i];
      }
      if (entry.size) sizes.push(entry);
    });
    if (!sizes.length) return null;

    /* Kategorien kan læses af kolonnerne: findes talje og benlængde men
       intet bryst, er det bukser. Så slipper vi for et ekstra felt i
       Shopify, indtil der er en vare hvor gætte-reglen ikke holder. */
    var has = function (f) { return fields.indexOf(f) !== -1; };
    var category = raw.category;
    if (!category) category = (!has('chest') && (has('waist') || has('inseam'))) ? 'bottoms' : 'tops';

    return {
      sizes: sizes,
      unit: unit,
      category: category,
      intendedFit: raw.intendedFit || 'relaxed',
      measurementType: raw.measurementType || 'garment_flat'
    };
  }

  return {
    recommend: recommend,
    parseGuide: parseGuide,
    SIZE_ORDER: SIZE_ORDER,
    /* Udstillet så testene kan ramme delene hver for sig. */
    _internal: { estimateBody: estimateBody, lookupBody: lookupBody, guideIsPlausible: guideIsPlausible,
                 idx: idx, BODY: BODY, fieldFor: fieldFor }
  };
});
